import {
  gramsPerLiterOf,
  LAYOUT_WIDTHS,
  layoutDepth,
  lbPerBbl,
  TANK_KINDS,
  TANK_UNITS,
  USUAL_LAYOUT_WIDTH,
} from "@bl/domain";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutGrid, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  type BrewSystem,
  type CellarSettings,
  keys,
  newKey,
  send,
  type Tank,
  type TankUnit,
  useBrewSystems,
  useCellarSettings,
  useChange,
  useTanks,
} from "../api";
import { decimalOnly } from "../brewPulls";
import { TankFloor } from "../components/TankFloor";
import { Dialog, DialogForm, ErrorNote, Field } from "../components/ui";
import { ZoomSlider } from "../components/ZoomSlider";
import { showDate } from "../format";
import { BrewSystemDialog, systemSummary } from "../forms/BatchFields";
import { useNotify } from "../notify";
import {
  byKind,
  capacityLabel,
  contentsOf,
  inCellar,
  kindPlural,
  narrowestWidth,
  type Spot,
  sizeOf,
  totalLabel,
} from "../tankLayout";
import { HiddenChip } from "./Production";

/**
 * Settings, Equipment (Tyler, 2026-10-04): the brewery's brewhouse and cellar in one place. Brew
 * systems are the brewhouse's usual batches (Scott, 2026-10-02), each a batch a recipe can pick to fill
 * its Batch settings; changing one here leaves the recipes already filled from it as they are. The
 * cellar is the brewery's own tanks (Scott's Jam, 2026-10-03), by kind, and the Brewery layout places
 * them on a floor plan of the room.
 */
export function Equipment() {
  const { data: systems = [], isLoading, error } = useBrewSystems();
  const { data: tanks = [], isLoading: loadingTanks, error: tanksError } = useTanks();
  const [editing, setEditing] = useState<BrewSystem | "new" | null>(null);
  const [deleting, setDeleting] = useState<BrewSystem | null>(null);
  const [editingTank, setEditingTank] = useState<Tank | "new" | null>(null);
  const [removingTank, setRemovingTank] = useState<Tank | null>(null);
  const [laying, setLaying] = useState(false);
  const cellar = inCellar(tanks);
  const retired = tanks.filter((t) => t.retired);

  return (
    <section id="equipment" className="section">
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>Equipment</h2>
      </div>
      <div className="section-body stack">
        <p className="muted" style={{ margin: 0 }}>
          Your brewhouse and your cellar. A recipe picks a brew system to fill its Batch settings,
          and each brew goes into the cellar's tanks from brew day.
        </p>

        <div id="brew-systems" className="equipment-group">
          <h3 className="equipment-heading">
            Brew systems <span className="faint">{systems.length || ""}</span>
          </h3>
          <ErrorNote error={error} />
          {isLoading ? <p className="muted">Loading</p> : null}
          {!isLoading && systems.length === 0 ? (
            <p className="faint">
              None yet. A brew system is your brewhouse's usual batch, boil, efficiency and losses
              under a name.
            </p>
          ) : null}
          {systems.length > 0 ? (
            <div className="list-rows">
              {systems.map((s) => (
                <div key={s.id} className="brew-system-row">
                  <div className="brew-system-text">
                    <strong>{s.name}</strong>
                    <span className="muted">{systemSummary(s)}</span>
                    <span className="faint">
                      {s.recipes === 0
                        ? "No recipes yet"
                        : `${s.recipes} ${s.recipes === 1 ? "recipe" : "recipes"}`}
                    </span>
                  </div>
                  <button type="button" className="link-button" onClick={() => setEditing(s)}>
                    Change
                  </button>
                  <button
                    type="button"
                    className="link-button danger-link"
                    onClick={() => setDeleting(s)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div id="cellar" className="equipment-group">
          <h3 className="equipment-heading">
            Cellar <span className="faint">{cellar.length || ""}</span>
          </h3>
          <ErrorNote error={tanksError} />
          {loadingTanks ? <p className="muted">Loading</p> : null}
          {!loadingTanks && cellar.length === 0 ? (
            <p className="faint">
              No tanks yet. Add your fermenters and brite tanks, and each brew can be put in one on
              brew day.
            </p>
          ) : null}
          {byKind(cellar).map((group) => (
            <div key={group.kind} className="equipment-kind">
              <h4>
                {kindPlural(group.kind)} <span className="faint">{group.tanks.length}</span>
              </h4>
              <div className="list-rows">
                {group.tanks.map((t) => (
                  <div key={t.id} className="brew-system-row">
                    <div className="brew-system-text">
                      <span className="row" style={{ gap: 8 }}>
                        <strong>{t.name}</strong>
                        <span className="muted">
                          {capacityLabel(t)}
                          {t.totalVolume === null ? "" : `, ${totalLabel(t)} total`}
                        </span>
                        {t.outOfService ? <span className="chip warn">Out of service</span> : null}
                        {t.hidden ? <HiddenChip /> : null}
                      </span>
                      <span className="faint">{tankLine(t)}</span>
                    </div>
                    <button type="button" className="link-button" onClick={() => setEditingTank(t)}>
                      Change
                    </button>
                    <button
                      type="button"
                      className="link-button danger-link"
                      onClick={() => setRemovingTank(t)}
                    >
                      {t.used ? "Retire" : "Delete"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {retired.length ? <RetiredTanks tanks={retired} /> : null}
        </div>

        <div className="row">
          <button type="button" className="button" onClick={() => setEditing("new")}>
            <Plus size={16} /> Add brew system
          </button>
          <button type="button" className="button" onClick={() => setEditingTank("new")}>
            <Plus size={16} /> Add cellar tank
          </button>
          <button
            type="button"
            className="button"
            disabled={cellar.length === 0}
            title={cellar.length === 0 ? "Add a tank first" : undefined}
            onClick={() => setLaying(true)}
          >
            <LayoutGrid size={16} /> Brewery layout
          </button>
        </div>

        <RepitchLimits />
      </div>
      {editing ? (
        <BrewSystemDialog
          key={editing === "new" ? "new" : editing.id}
          open
          onClose={() => setEditing(null)}
          system={editing === "new" ? undefined : editing}
        />
      ) : null}
      {deleting ? (
        <DeleteDialog key={deleting.id} system={deleting} onClose={() => setDeleting(null)} />
      ) : null}
      {editingTank ? (
        <TankDialog
          key={editingTank === "new" ? "new" : editingTank.id}
          tank={editingTank === "new" ? undefined : editingTank}
          tanks={tanks}
          onClose={() => setEditingTank(null)}
        />
      ) : null}
      {removingTank ? (
        <RemoveTankDialog
          key={removingTank.id}
          tank={removingTank}
          onClose={() => setRemovingTank(null)}
        />
      ) : null}
      {laying ? <LayoutDialog tanks={cellar} onClose={() => setLaying(false)} /> : null}
    </section>
  );
}

/**
 * When brew day warns about repitching a beer's yeast (Scott's Jam, 2026-10-08: "sometimes you will not
 * want to repitch if the gravity is to high, beer is too hoppy or the yeast has been stagnant for too
 * long without being fed"). A warning never stops it; an empty limit is never warned about.
 */
function RepitchLimits() {
  const { data: cellar } = useCellarSettings();
  if (!cellar) return null;
  return (
    <div id="repitching" className="equipment-group">
      <h3 className="equipment-heading">Repitching</h3>
      <RepitchForm
        key={`${cellar.repitchMaxOg}|${cellar.repitchMaxDryHop}|${cellar.repitchMaxDays}`}
        cellar={cellar}
      />
    </div>
  );
}

const typedOf = (cellar: CellarSettings) => ({
  og: cellar.repitchMaxOg === null ? "" : Number(cellar.repitchMaxOg).toFixed(3),
  dryHop:
    cellar.repitchMaxDryHop === null
      ? ""
      : String(Number(lbPerBbl(cellar.repitchMaxDryHop).toFixed(2))),
  days: cellar.repitchMaxDays === null ? "" : String(cellar.repitchMaxDays),
});

function RepitchForm({ cellar }: { cellar: CellarSettings }) {
  const notify = useNotify();
  const [typed, setTyped] = useState(() => typedOf(cellar));
  const was = typedOf(cellar);
  const changed = typed.og !== was.og || typed.dryHop !== was.dryHop || typed.days !== was.days;
  const save = useChange(
    (_: undefined, key) =>
      send.saveCellarSettings(
        {
          repitchMaxOg: typed.og.trim() || null,
          repitchMaxDryHop:
            typed.dryHop.trim() === "" ? null : gramsPerLiterOf(typed.dryHop.trim()).toFixed(9),
          repitchMaxDays: typed.days.trim() === "" ? null : Number(typed.days),
        },
        key,
      ),
    { onSuccess: () => notify("Repitch limits saved") },
  );
  return (
    <form
      className="repitch-limits"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (changed) save.mutate(undefined);
      }}
    >
      <p className="muted" style={{ margin: 0 }}>
        Brew day warns about repitching yeast from a beer past any of these, and still lets you pick
        it. Leave one empty to never warn about it.
      </p>
      <div className="repitch-limit-fields">
        <Field label="OG over" hint="Stressed yeast from a strong beer">
          <input
            className="input num"
            inputMode="decimal"
            autoComplete="off"
            placeholder="None"
            value={typed.og}
            onChange={(ev) => setTyped({ ...typed, og: decimalOnly(ev.target.value) })}
          />
        </Field>
        <Field label="Dry hopped over" hint="Hop matter in the cone">
          <span className="input-unit">
            <input
              className="input num"
              inputMode="decimal"
              autoComplete="off"
              placeholder="None"
              value={typed.dryHop}
              onChange={(ev) => setTyped({ ...typed, dryHop: decimalOnly(ev.target.value) })}
            />
            <span className="unit">lb/bbl</span>
          </span>
        </Field>
        <Field
          label="Unfed for over"
          hint="Since its beer's brew day in a fermenter, or its harvest in a brink"
        >
          <span className="input-unit">
            <input
              className="input num"
              inputMode="numeric"
              autoComplete="off"
              placeholder="None"
              value={typed.days}
              onChange={(ev) =>
                setTyped({ ...typed, days: ev.target.value.replace(/\D/g, "").slice(0, 3) })
              }
            />
            <span className="unit">days</span>
          </span>
        </Field>
      </div>
      <div className="row">
        <button type="submit" className="button" disabled={!changed || save.isPending}>
          Save repitch limits
        </button>
      </div>
      <ErrorNote error={save.error} />
    </form>
  );
}

/** What a tank holds today, or what it is booked for next, in a line. */
export function tankLine(t: Tank): string {
  const { now, next } = contentsOf(t);
  if (now.length) {
    return now
      .map((s) => {
        const brew = s.brew.number === null ? "" : ` brew ${s.brew.number}`;
        const due = s.dueOutOn
          ? `, due out ${showDate(s.dueOutOn)}${s.overdue ? ", overdue" : ""}`
          : "";
        return `${s.brew.recipe.name}${brew} since ${showDate(s.inOn)}${due}`;
      })
      .join("; ");
  }
  if (next) return `Empty. Booked from ${showDate(next.inOn)} for ${next.brew.recipe.name}`;
  return "Empty";
}

function DeleteDialog({ system, onClose }: { system: BrewSystem; onClose: () => void }) {
  const notify = useNotify();
  const remove = useChange((_: undefined, key) => send.deleteBrewSystem(system.id, key), {
    onSuccess: () => {
      notify(`${system.name} deleted`);
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} title={`Delete ${system.name}?`}>
      <DialogForm
        onSubmit={() => remove.mutate(undefined)}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Keep it
            </button>
            <button type="submit" className="button danger-fill" disabled={remove.isPending}>
              Delete
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          {system.recipes === 0
            ? "No recipe uses it, so nothing else changes."
            : `The ${system.recipes === 1 ? "recipe" : `${system.recipes} recipes`} filled from it keep every figure they have, and no longer name a brew system.`}
        </p>
        <ErrorNote error={remove.error} />
      </DialogForm>
    </Dialog>
  );
}

const ANOTHER = "__another";

/**
 * Add a tank to the cellar, or change one: its name, kind, size and total volume, and whether it is in
 * use. The total volume is the room above its size the beer rises into as it ferments (Scott,
 * 2026-10-08), typed in the size's unit.
 */
function TankDialog({ tank, tanks, onClose }: { tank?: Tank; tanks: Tank[]; onClose: () => void }) {
  const notify = useNotify();
  // The brewery's own kinds, from its tanks, offered beside the built-in ones.
  const own = [...new Set(tanks.map((t) => t.kind))].filter(
    (k) => !(TANK_KINDS as readonly string[]).includes(k),
  );
  const kinds = [...TANK_KINDS, ...own];
  // A new tank starts in the unit most of the cellar is measured in; barrels in an empty one.
  const usual = TANK_UNITS.reduce<TankUnit>(
    (best, u) =>
      tanks.filter((t) => t.capacity.unit === u).length >
      tanks.filter((t) => t.capacity.unit === best).length
        ? u
        : best,
    "bbl",
  );
  const [name, setName] = useState(tank?.name ?? "");
  const [kind, setKind] = useState(tank?.kind ?? "Fermenter");
  const [anotherKind, setAnotherKind] = useState("");
  const [value, setValue] = useState(tank?.capacity.value ?? "");
  const [unit, setUnit] = useState<TankUnit>(tank?.capacity.unit ?? usual);
  const [total, setTotal] = useState(tank?.totalVolume ?? "");
  const [outOfService, setOutOfService] = useState(tank?.outOfService ?? false);
  const [hidden, setHidden] = useState(tank?.hidden ?? false);
  const [tried, setTried] = useState(false);
  const kindTyped = kind === ANOTHER ? anotherKind.trim() : kind;
  const problems = [
    name.trim() ? null : "a name",
    kindTyped ? null : "a kind",
    Number(value) > 0 ? null : "a size",
    total.trim() === "" || Number(total) >= Number(value)
      ? null
      : "a total volume at least its size",
  ].filter((p): p is string => p !== null);
  const save = useChange(
    (_: undefined, key) => {
      const body = {
        name: name.trim(),
        kind: kindTyped,
        capacity: { value: value.trim(), unit },
        totalVolume: total.trim() === "" ? null : total.trim(),
      };
      return tank
        ? send.saveTank(
            {
              id: tank.id,
              // Hidden only when changed here, so one hidden from the schedule meanwhile stays so.
              patch: { ...body, outOfService, ...(hidden !== tank.hidden ? { hidden } : {}) },
            },
            key,
          )
        : send.createTank(body, key);
    },
    {
      onSuccess: (saved) => {
        notify(tank ? `${saved.name} saved` : `${saved.name} added to the cellar`);
        onClose();
      },
    },
  );
  return (
    <Dialog open onClose={onClose} title={tank ? `Change ${tank.name}` : "New cellar tank"}>
      <DialogForm
        onSubmit={() => {
          setTried(true);
          if (problems.length === 0) save.mutate(undefined);
        }}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={save.isPending}>
              {tank ? "Save" : "Add tank"}
            </button>
          </>
        }
      >
        <Field label="Name" hint="What it's called on the floor, like FV1 or BT2">
          <input
            className="input"
            value={name}
            maxLength={40}
            autoFocus={!tank}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Kind">
          <select className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
            <option value={ANOTHER}>Another kind</option>
          </select>
        </Field>
        {kind === ANOTHER ? (
          <Field label="What kind" hint="Unitank, Serving tank, Barrel, or whatever you call it">
            <input
              className="input"
              value={anotherKind}
              maxLength={40}
              autoFocus
              onChange={(e) => setAnotherKind(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label="Size" hint="What you call it, like 15 bbl">
          <div className="quantity-input">
            <input
              className="input num"
              inputMode="decimal"
              autoComplete="off"
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/[^0-9.]/g, ""))}
            />
            <select
              className="select"
              aria-label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value as TankUnit)}
            >
              {TANK_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field
          label="Total volume"
          hint="To the top, headspace and all. A brew is flagged past 90% of it, or past the size if this is left empty."
        >
          <span className="input-unit">
            <input
              className="input num"
              inputMode="decimal"
              autoComplete="off"
              aria-label="Total volume"
              value={total}
              onChange={(e) => setTotal(e.target.value.replace(/[^0-9.]/g, ""))}
            />
            <span className="unit">{unit}</span>
          </span>
        </Field>
        {tank ? (
          <label className="check-row">
            <input
              type="checkbox"
              checked={outOfService}
              onChange={(e) => setOutOfService(e.target.checked)}
            />
            <span>
              Out of service
              <span className="faint">
                {" "}
                (its glycol is broken, say). Nothing new goes into it until it is back in use.
              </span>
            </span>
          </label>
        ) : null}
        {tank ? (
          <label className="check-row">
            <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
            <span>
              Hidden from the tank schedule
              <span className="faint">
                {" "}
                on Production, for everyone. It still holds beer and takes bookings.
              </span>
            </span>
          </label>
        ) : null}
        {tried && problems.length ? (
          <div className="form-error">Still needs {problems.join(", ")}.</div>
        ) : null}
        <ErrorNote error={save.error} />
      </DialogForm>
    </Dialog>
  );
}

/** A tank never used is deleted; one a brew has used is retired, so its brews still name it. */
function RemoveTankDialog({ tank, onClose }: { tank: Tank; onClose: () => void }) {
  const notify = useNotify();
  const remove = useChange(
    async (_: undefined, key): Promise<unknown> =>
      tank.used
        ? send.saveTank({ id: tank.id, patch: { retired: true } }, key)
        : send.deleteTank(tank.id, key),
    {
      onSuccess: () => {
        notify(tank.used ? `${tank.name} retired` : `${tank.name} deleted`);
        onClose();
      },
    },
  );
  return (
    <Dialog open onClose={onClose} title={`${tank.used ? "Retire" : "Delete"} ${tank.name}?`}>
      <DialogForm
        onSubmit={() => remove.mutate(undefined)}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Keep it
            </button>
            <button type="submit" className="button danger-fill" disabled={remove.isPending}>
              {tank.used ? "Retire" : "Delete"}
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          {tank.used
            ? "It leaves the cellar, the tank pickers and the Brewery layout. The brews it held still say they were in it. A tank only out for repair can be marked Out of service instead."
            : "No brew has used it, so nothing else changes."}
        </p>
        <ErrorNote error={remove.error} />
      </DialogForm>
    </Dialog>
  );
}

function RetiredTanks({ tanks }: { tanks: Tank[] }) {
  const notify = useNotify();
  const back = useChange(
    (t: Tank, key) => send.saveTank({ id: t.id, patch: { retired: false } }, key),
    { onSuccess: (t) => notify(`${t.name} is back in the cellar`) },
  );
  return (
    <div className="retired-tanks">
      <span className="faint">Retired:</span>
      {tanks.map((t) => (
        <span key={t.id} className="row" style={{ gap: 6 }}>
          <span className="muted">
            {t.name}, {capacityLabel(t)}
          </span>
          <button
            type="button"
            className="link-button"
            disabled={back.isPending}
            onClick={() => back.mutate(t)}
          >
            Bring back
          </button>
        </span>
      ))}
      <ErrorNote error={back.error} />
    </div>
  );
}

/**
 * The Brewery layout (Tyler, 2026-10-04): every tank in the cellar on a floor plan, dragged around
 * until it looks like the room. Each place is saved as it is dropped, for everyone in the brewery. Its
 * zoom is how many squares across the floor is (Tyler, 2026-10-05: "a zoom function to increase
 * brewery size ... the view under production can scale to that zoom level"), saved the same way.
 */
function LayoutDialog({ tanks, onClose }: { tanks: Tank[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: settings } = useCellarSettings();
  // Where tanks were just put, ahead of the server's answer, so a drop lands where it was let go.
  const [moved, setMoved] = useState<Map<string, Spot | null>>(new Map());
  // The zoom just chosen, likewise.
  const [zoomed, setZoomed] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const timers = useRef(new Map<string, { timer: number; save: () => void }>());
  const spotOf = (t: Tank) => (moved.has(t.id) ? (moved.get(t.id) ?? null) : t.layout);
  const width = zoomed ?? settings?.layoutWidth ?? USUAL_LAYOUT_WIDTH;
  // Tanks keep their squares from the top left, so it zooms in only as far as they all still fit.
  const narrowest = narrowestWidth(
    tanks.flatMap((t) => {
      const spot = spotOf(t);
      return spot ? [{ spot, size: sizeOf(t.capacityLiters) }] : [];
    }),
    Math.max(0, ...tanks.map((t) => sizeOf(t.capacityLiters))),
  );
  const min = Math.max(0, (LAYOUT_WIDTHS as readonly number[]).indexOf(narrowest));

  const persist = async (id: string, spot: Spot | null) => {
    try {
      // Each place its own change: two tanks dropped one after the other are two saves.
      await send.saveTank({ id, patch: { layout: spot } }, newKey());
      setError(null);
      await queryClient.invalidateQueries({ queryKey: keys.tanks });
    } catch (e) {
      setError(e);
      setMoved((m) => {
        const next = new Map(m);
        next.delete(id);
        return next;
      });
    }
  };

  // Arrow keys move a tank a square at a time; it is saved once they stop.
  const place = (t: Tank, spot: Spot | null, wait = 0) => {
    setMoved((m) => new Map(m).set(t.id, spot));
    later(t.id, () => persist(t.id, spot), wait);
  };

  const zoom = (to: number) => {
    setZoomed(to);
    // Stepped through quickly, only where it stops is saved.
    later(
      "zoom",
      async () => {
        try {
          await send.saveCellarSettings({ layoutWidth: to }, newKey());
          setError(null);
          await queryClient.invalidateQueries({ queryKey: keys.cellarSettings });
        } catch (e) {
          setError(e);
          setZoomed(null);
        }
      },
      400,
    );
  };

  const later = (key: string, run: () => Promise<void>, wait: number) => {
    const pending = timers.current.get(key);
    if (pending) window.clearTimeout(pending.timer);
    const save = () => {
      timers.current.delete(key);
      void run();
    };
    timers.current.set(key, { timer: window.setTimeout(save, wait), save });
  };

  // Closed with a move still waiting, it is saved anyway.
  useEffect(
    () => () => {
      for (const { timer, save } of timers.current.values()) {
        window.clearTimeout(timer);
        save();
      }
    },
    [],
  );

  return (
    <Dialog open onClose={onClose} title="Brewery layout" wide>
      <DialogForm
        onSubmit={onClose}
        footer={
          <button type="submit" className="button primary">
            Done
          </button>
        }
      >
        <p className="muted" style={{ margin: 0 }}>
          Drag each tank to where it stands in the brewery. Drag one off the floor to take it off
          the layout. Zoom out for a bigger brewery, or in to draw each tank bigger; Production
          shows the layout the same way. Each change is saved as you make it, for everyone in the
          brewery.
        </p>
        <div className="layout-zoom">
          <span className="muted">Zoom</span>
          <ZoomSlider
            stops={LAYOUT_WIDTHS}
            value={width as (typeof LAYOUT_WIDTHS)[number]}
            onZoom={zoom}
            labelOf={(w) => `${w} × ${layoutDepth(w)}`}
            name="Squares across the floor"
            less="Zoom in: a smaller floor, each tank bigger"
            more="Zoom out: a bigger floor, room for more tanks"
            min={min}
          />
        </div>
        {min > 0 && width <= narrowest ? (
          <p className="faint" style={{ margin: 0 }}>
            Zoomed in as far as the tanks allow. Move tanks in from the right or bottom edge to zoom
            in further.
          </p>
        ) : null}
        <TankFloor
          tanks={tanks}
          width={width}
          spotOf={spotOf}
          onPlace={(t, spot, nudge) => place(t, spot, nudge ? 400 : 0)}
        />
        <ErrorNote error={error} />
      </DialogForm>
    </Dialog>
  );
}
